/**
 * MED-938 / E3.5 — shared pure helpers for the two-column participant dashboard
 * refresh: date formatting, status-pill derivation, and the Needs Attention
 * item assembly (the one place that decides which items show and in what
 * priority order). The DateBlock component lives in DateBlock.tsx.
 */
import type {
  DashboardRefreshCompletedSession,
  DashboardRefreshUpcomingSession,
  ParticipantActionItem,
  ParticipantDashboardData,
} from '../../../services/api/participantPortal.js';
import type { DateBlockTone } from './DateBlock.js';
import { composeSessionLabel } from '../sessionLabel.js';
import { getZoneCode } from '../../../utils/formatters';

// ---------------------------------------------------------------------------
// Date / time formatting
// ---------------------------------------------------------------------------

/** Long header date, e.g. "Tuesday, March 18, 2024". */
export function formatLongDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/** "Thu 3:00 PM" */
export function formatDayTime(iso: string): string {
  const d = new Date(iso);
  // MED-1389: name the zone ("9:00 AM ET"), as the coach view does.
  return `${d.toLocaleDateString('en-US', { weekday: 'short' })} ${d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })} ${getZoneCode(null, d)}`;
}

/**
 * Weekday + date + time used in deadlines, e.g. "Mon, Jul 13, 9:00 AM".
 *
 * MED-1144 — the date was added (was "Monday 9:00 AM") so a reflection due for a
 * session days or weeks out reads unambiguously instead of looking imminent
 * (closes UAT #3). The deadline VALUE is unchanged — still derived from the
 * session's `scheduled_start`; only the display gains the date.
 */
export function formatDeadline(iso: string): string {
  const d = new Date(iso);
  const date = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${date}, ${time} ${getZoneCode(null, d)}`;
}

export function formatDuration(minutes: number | null): string | null {
  if (minutes == null) return null;
  return `${minutes} min`;
}

/** Whole-day difference from start-of-today to the session's calendar day. */
function dayDiff(iso: string): number {
  const d = new Date(iso);
  const sessionDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((sessionDay.getTime() - today.getTime()) / 86_400_000);
}

// ---------------------------------------------------------------------------
// Date block tone + upcoming status badge
// ---------------------------------------------------------------------------

/** Green = today/live, amber = tomorrow/pending reschedule, blue = further out. */
export function upcomingDateTone(session: DashboardRefreshUpcomingSession): DateBlockTone {
  // MED-1062 (E4.0h): a missed session is pending reschedule — amber, not the
  // green/blue "it's coming up" tones (its date is in the past).
  if (session.status === 'missed') return 'amber';
  if (session.is_live) return 'green';
  const diff = dayDiff(session.scheduled_start);
  if (diff <= 0) return 'green';
  if (diff === 1) return 'amber';
  return 'blue';
}

export interface UpcomingBadge {
  label: string;
  color: string;
  dot: boolean;
}

export function upcomingBadge(session: DashboardRefreshUpcomingSession): UpcomingBadge {
  // MED-1062 (E4.0h): a no-show 'missed' peer-led session stays visible in
  // Upcoming with an amber "PENDING RESCHEDULE" pill (participant-facing label;
  // the CM/system badge stays "MISSED" in SESSION_STATUS_CONFIG). Reuses the
  // existing amber (warningAmber) treatment — label change only.
  if (session.status === 'missed') {
    return { label: 'PENDING RESCHEDULE', color: 'warningAmber', dot: false };
  }
  if (session.is_live) return { label: 'LIVE NOW', color: 'medleyGreen', dot: true };
  const diff = dayDiff(session.scheduled_start);
  if (diff <= 0) return { label: 'Today', color: 'warningAmber', dot: true };
  if (diff === 1) return { label: 'Tomorrow', color: 'warningAmber', dot: true };
  if (diff < 7) return { label: `In ${diff} days`, color: 'gray', dot: false };
  const weeks = Math.round(diff / 7);
  return { label: `In ${weeks} ${weeks === 1 ? 'week' : 'weeks'}`, color: 'gray', dot: false };
}

/**
 * Session label, e.g. "Session 3: Navigating Change".
 *
 * MED-1146 — delegates to the shared participant `composeSessionLabel` so the
 * dashboard renders the display-aware topic (or the group name when there is no
 * topic) and never prepends "Session N:" onto the raw `{group} - Session N`
 * title (the residual double-label MED-1134 left behind).
 */
export function sessionLabel(session: {
  session_number: number | null;
  title: string;
  session_topic?: string | null;
  group_name?: string | null;
}): string {
  return composeSessionLabel(session);
}

// ---------------------------------------------------------------------------
// Needs Attention assembly
// ---------------------------------------------------------------------------

export type NeedsAttentionItem =
  | { kind: 'peer_leader_backup'; key: string; sessionId: string; title: string; subtitle: string }
  | { kind: 'peer_leader_lead'; key: string; sessionId: string; title: string; subtitle: string }
  | { kind: 'reflection'; key: string; sessionId: string; title: string; subtitle: string }
  | { kind: 'feedback'; key: string; sessionId: string; title: string; subtitle: string }
  // MED-1145: peer-leader return path into the post-session page (incl. recap
  // email) for a session they LED — parity with the coach `session_report_ready`
  // item. Carries cohortId for the cohort-scoped PeerLeaderPostSessionPage route.
  | {
      kind: 'return_to_post_session';
      key: string;
      sessionId: string;
      cohortId: string;
      title: string;
      subtitle: string;
    }
  | {
      kind: 'schedule_coaching';
      key: string;
      coachId: string;
      title: string;
      subtitle: string;
      disabled: boolean;
    }
  | {
      kind: 'schedule_checkin';
      key: string;
      coachId: string;
      huddleType: 'intro' | 'midpoint_checkin';
      title: string;
      subtitle: string;
      disabled: boolean;
    };

function actionItemCopy(item: ParticipantActionItem): { title: string; subtitle: string } {
  switch (item.huddle_type) {
    case 'intro':
      return { title: 'Schedule your Intro Check-in', subtitle: 'Meet your coach before you begin' };
    case 'midpoint_checkin':
      return {
        title: 'Schedule your Midpoint Check-in',
        subtitle: 'Reflect on your progress so far',
      };
    default:
      return {
        title: 'Schedule your 1:1 coaching',
        subtitle: `${item.unscheduled_count} session${item.unscheduled_count === 1 ? '' : 's'} to schedule`,
      };
  }
}

/**
 * Build the ordered Needs Attention list. Priority (most urgent first):
 * backup-needed peer-leader prompt → leading prep prompt → reflection due →
 * feedback due → return to a led session's post-session page (MED-1145) →
 * 1:1/check-in scheduling (carried over from Action Items per the MED-938 scope
 * decision). A session the participant is leading shows the combined lead
 * prompt, never a separate reflection item. The MED-1145 return item is a
 * lower-urgency "review" prompt, mirroring the coach `session_report_ready`
 * placement (below feedback).
 */
export function buildNeedsAttentionItems(data: ParticipantDashboardData): NeedsAttentionItem[] {
  const refresh = data.refresh;
  if (!refresh) return [];

  const backups: NeedsAttentionItem[] = [];
  const leads: NeedsAttentionItem[] = [];
  const reflections: NeedsAttentionItem[] = [];

  for (const s of refresh.upcoming_sessions) {
    // MED-1062 (E4.0h): a no-show 'missed' session is pending reschedule — it
    // must not surface any actionable prompt (lead / backup / reflection). It
    // stays visible only as the read-only "PENDING RESCHEDULE" row in Upcoming.
    if (s.status === 'missed') continue;
    if (s.peer_leader_state === 'backup_needed') {
      backups.push({
        kind: 'peer_leader_backup',
        key: `backup-${s.id}`,
        sessionId: s.id,
        title:
          s.session_number != null
            ? `Can you lead Session ${s.session_number}?`
            : 'Can you lead your session?',
        subtitle: `Your group needs a leader for ${formatDayTime(s.scheduled_start)}`,
      });
      continue;
    }
    // MED-1144 (QA follow-up): gate the lead prompt on prep_unlocked too — a
    // leader whose prep is still sequence-locked ("Session N−1 must complete")
    // must not be nagged to start prep they can't reach (Ben's UAT dead-end).
    // The Leading badge in Upcoming persists; only this actionable prompt waits.
    if (s.is_leading && s.prep_outstanding && s.prep_unlocked) {
      leads.push({
        kind: 'peer_leader_lead',
        key: `lead-${s.id}`,
        sessionId: s.id,
        title: 'Get ready to lead your session',
        subtitle: `${sessionLabel(s)} · You're leading ${formatDayTime(s.scheduled_start)}`,
      });
      continue;
    }
    // Reflection due — group sessions only, available + not complete, and not
    // the session being led (that prompt is combined above).
    if (
      s.modality_type === 'group_coaching' &&
      s.reflection_available &&
      !s.reflection_complete &&
      !s.is_leading
    ) {
      reflections.push({
        kind: 'reflection',
        key: `reflection-${s.id}`,
        sessionId: s.id,
        title: 'Complete your pre-session reflection',
        subtitle: `${sessionLabel(s)} · Due before ${formatDeadline(s.scheduled_start)}`,
      });
    }
  }

  // MED-1063: the feedback prompt is deferred + attendance-aware. Surface it
  // only when (a) feedback isn't done, (b) the participant wasn't marked absent
  // — gate on "not absent" so partial attendees still qualify (Decision 1) —
  // and (c) the next calendar day has begun (feedback_prompt_available, AC2).
  // Page availability is separate and immediate (AC1); this only governs the nudge.
  const feedback: NeedsAttentionItem[] = refresh.completed_sessions
    .filter(c => !c.feedback_complete && !c.is_absent && c.feedback_prompt_available)
    .map((c: DashboardRefreshCompletedSession) => ({
      kind: 'feedback' as const,
      key: `feedback-${c.id}`,
      sessionId: c.id,
      title:
        c.session_number != null ? `Session ${c.session_number} feedback` : `${c.title} feedback`,
      subtitle: 'Share your thoughts · Takes 2 min',
    }));

  // MED-1145 (AC1, AC4): a peer leader's return path into the post-session page
  // for a session they LED. Parity with the coach `session_report_ready` item —
  // scope is the single MOST-RECENT completed led session (completed_sessions is
  // ordered most-recent first), not every past led session. Additive: the
  // non-leader `feedback` item above is untouched (AC5), and both can coexist
  // for the same session just as they do on the coach dashboard.
  const recentLed = refresh.completed_sessions.find(c => c.is_leading);
  const returnToPostSession: NeedsAttentionItem[] = recentLed
    ? [
        {
          kind: 'return_to_post_session' as const,
          key: `recap-${recentLed.id}`,
          sessionId: recentLed.id,
          cohortId: recentLed.cohort_id,
          title:
            recentLed.session_number != null
              ? `Session ${recentLed.session_number} recap`
              : `${recentLed.title} recap`,
          subtitle: 'Send or edit the recap email · You led this session',
        },
      ]
    : [];

  const scheduling: NeedsAttentionItem[] = (data.action_items ?? []).map(item => {
    const copy = actionItemCopy(item);
    const disabled = !item.coach_calendly_connected;
    if (item.huddle_type === 'intro' || item.huddle_type === 'midpoint_checkin') {
      return {
        kind: 'schedule_checkin' as const,
        key: `checkin-${item.coach_id}-${item.huddle_type}`,
        coachId: item.coach_id,
        huddleType: item.huddle_type,
        title: copy.title,
        subtitle: copy.subtitle,
        disabled,
      };
    }
    return {
      kind: 'schedule_coaching' as const,
      key: `coaching-${item.coach_id}`,
      coachId: item.coach_id,
      title: copy.title,
      subtitle: copy.subtitle,
      disabled,
    };
  });

  return [
    ...backups,
    ...leads,
    ...reflections,
    ...feedback,
    ...returnToPostSession,
    ...scheduling,
  ];
}
